import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import api from '../api';
import KanbanBoard from '../components/KanbanBoard';

// ─── Shared input styles (16px prevents iOS zoom) ────────────────────────────
const inp = {
  width: '100%', background: '#ffffff', border: '1px solid #dde1e7',
  borderRadius: 8, color: '#1a1d23', padding: '16px', fontSize: 16,
  fontFamily: 'IBM Plex Sans, sans-serif', lineHeight: 1.4,
  boxSizing: 'border-box', WebkitAppearance: 'none', appearance: 'none',
};
const inpFocus = { borderColor: '#1a6eb5', outline: 'none' };

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#8a9ab0', marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}
function Sel({ value, onChange, children }) {
  const [f, setF] = useState(false);
  return (
    <select value={value} onChange={onChange} onFocus={() => setF(true)} onBlur={() => setF(false)}
      style={{ ...inp, ...(f ? inpFocus : {}), cursor: 'pointer' }}>{children}</select>
  );
}
function Inp({ value, onChange, type = 'text', ...rest }) {
  const [f, setF] = useState(false);
  return (
    <input type={type} value={value} onChange={onChange}
      onFocus={() => setF(true)} onBlur={() => setF(false)}
      style={{ ...inp, ...(f ? inpFocus : {}) }} {...rest} />
  );
}
function Toggle({ label, sub, value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', marginBottom: 16,
      background: value ? 'rgba(26,110,181,0.08)' : '#ffffff',
      border: value ? '1px solid rgba(26,110,181,0.35)' : '1px solid #dde1e7',
      borderRadius: 10, padding: '16px',
    }}>
      <div style={{ width: 48, height: 26, borderRadius: 13, flexShrink: 0, background: value ? '#1a6eb5' : '#dde1e7', position: 'relative', transition: 'background 0.2s' }}>
        <div style={{ position: 'absolute', top: 3, left: value ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: value ? '#fff' : '#4a5568', transition: 'left 0.2s' }} />
      </div>
      <div>
        <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 15, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: value ? '#1a6eb5' : '#1a1d23' }}>{label}</div>
        {sub && <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#8a9ab0', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}
function Toast({ msg, type, onDismiss }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [msg, onDismiss]);
  if (!msg) return null;
  const ok = type === 'success';
  return (
    <div style={{
      position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
      zIndex: 3000, maxWidth: 400, width: 'calc(100% - 32px)',
      background: ok ? 'rgba(30,126,62,0.15)' : 'rgba(192,57,43,0.15)',
      border: `1px solid ${ok ? 'rgba(30,126,62,0.4)' : 'rgba(192,57,43,0.4)'}`,
      borderRadius: 10, padding: '14px 18px', color: ok ? '#1e7e3e' : '#c0392b',
      fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    }}>
      <span>{ok ? '✓  ' : '✗  '}{msg}</span>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}

function elapsed(startedAt, now) {
  const ms = now - new Date(startedAt).getTime();
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

const NON_PRODUCTIVE_REASONS = [
  'Machine downtime',
  'Waiting for materials',
  'Training',
  'Cleaning',
  'Meeting',
  'Other',
];

// ─── Clock On/Off using DB sessions ──────────────────────────────────────────
function ClockForm({ projects, workCentres, staffId, onLogged }) {
  const [form, setForm]   = useState({ work_centre_id: '', project_id: '', non_productive: false, non_productive_reason: '', description: '' });
  const [sessions, setSessions] = useState([]);
  const [now, setNow]     = useState(Date.now());
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadSessions = useCallback(() => {
    api.get('/clock-sessions').then(r => setSessions(r.data.value || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadSessions();
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, [loadSessions]);

  const myActiveSession = sessions.find(s => s.staff_id === staffId) || null;

  const clockOn = async () => {
    if (!form.work_centre_id) {
      setToast({ msg: 'Select a work centre.', type: 'error' }); return;
    }
    if (!form.non_productive && !form.project_id) {
      setToast({ msg: 'Select a project, or enable Non-Productive.', type: 'error' }); return;
    }
    if (form.non_productive && !form.non_productive_reason) {
      setToast({ msg: 'Select a reason for non-productive time.', type: 'error' }); return;
    }
    if (form.non_productive && form.non_productive_reason === 'Other' && !form.description.trim()) {
      setToast({ msg: 'Please specify the reason.', type: 'error' }); return;
    }
    if (myActiveSession) {
      setToast({ msg: 'You are already clocked on to another task — please clock off first', type: 'error' }); return;
    }
    try {
      const resp = await api.post('/clock-sessions', {
        staff_id:       staffId,
        work_centre_id: parseInt(form.work_centre_id),
        project_id:     form.non_productive ? null : parseInt(form.project_id),
        non_productive: form.non_productive,
        non_productive_reason: form.non_productive
          ? (form.non_productive_reason === 'Other' ? form.description.trim() : form.non_productive_reason)
          : null,
        description:    form.description || null,
      });
      setSessions(prev => [...prev, resp.data]);
      const time = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      setToast({ msg: `${resp.data.staff_name} clocked on at ${time}`, type: 'success' });
      set('project_id', ''); set('description', ''); set('non_productive_reason', '');
    } catch (e) {
      if (e.response?.status === 409) {
        setToast({ msg: 'You are already clocked on to another task — please clock off first', type: 'error' });
        loadSessions();
      } else {
        setToast({ msg: e.response?.data?.error || e.message, type: 'error' });
      }
    }
  };

  const clockOff = async (session) => {
    setSaving(session.id);
    try {
      const resp = await api.delete(`/clock-sessions/${session.id}`);
      setSessions(prev => prev.filter(s => s.id !== session.id));
      setToast({ msg: `${resp.data.staff_name} clocked off — ${resp.data.hours_logged.toFixed(2)}h logged`, type: 'success' });
      onLogged();
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, type: 'error' });
    }
    setSaving(null);
  };

  const selProject = !form.non_productive && form.project_id
    ? projects.find(p => p.id === parseInt(form.project_id)) : null;

  return (
    <div>
      <Toast msg={toast?.msg} type={toast?.type} onDismiss={() => setToast(null)} />
      <Toggle label="Non-Productive Time" sub="Clean down, shop work, training, etc."
        value={form.non_productive} onChange={v => { set('non_productive', v); set('project_id', ''); set('non_productive_reason', ''); set('description', ''); }} />

      {myActiveSession && (
        <div style={{ background: 'rgba(26,110,181,0.12)', border: '1px solid #1a6eb5', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 15, fontWeight: 700, color: '#1a6eb5', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
            ⚠ Already clocked on
          </div>
          <div style={{ fontSize: 13, color: '#4a5568' }}>
            {myActiveSession.non_productive
              ? (myActiveSession.non_productive_reason || myActiveSession.description || 'Non-productive time')
              : `${myActiveSession.job_number} — ${myActiveSession.project_title}`}
            {' · '}{myActiveSession.work_centre_name} · {elapsed(myActiveSession.started_at, now)} ago
          </div>
        </div>
      )}

      <Field label="Work Centre *">
        <Sel value={form.work_centre_id} onChange={e => set('work_centre_id', e.target.value)}>
          <option value="">Select work centre...</option>
          {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
        </Sel>
      </Field>
      {form.non_productive ? (
        <>
          <Field label="Reason *">
            <Sel value={form.non_productive_reason} onChange={e => set('non_productive_reason', e.target.value)}>
              <option value="">Select reason...</option>
              {NON_PRODUCTIVE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </Sel>
          </Field>
          {form.non_productive_reason === 'Other' && (
            <Field label="Please specify *">
              <Inp value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Describe the reason..." />
            </Field>
          )}
        </>
      ) : (
        <>
          <Field label="Project *">
            <Sel value={form.project_id} onChange={e => set('project_id', e.target.value)}>
              <option value="">Select project...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
            </Sel>
          </Field>
          {selProject && (
            <div style={{ background: '#f5f6f8', border: '1px solid #1a6eb5', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#1a6eb5', fontSize: 15, fontWeight: 700 }}>{selProject.job_number}</span>
              <span style={{ fontSize: 13, color: '#4a5568', marginLeft: 10 }}>{selProject.customer_name}</span>
            </div>
          )}
        </>
      )}
      <button onClick={clockOn} disabled={!!myActiveSession} style={{
        width: '100%', padding: '20px', border: 'none', borderRadius: 10, marginBottom: 28,
        background: myActiveSession ? '#dde1e7' : '#1e7e3e', color: myActiveSession ? '#8a9ab0' : '#fff',
        fontFamily: 'Rajdhani, sans-serif', fontSize: 22, fontWeight: 700,
        letterSpacing: 2, textTransform: 'uppercase', cursor: myActiveSession ? 'not-allowed' : 'pointer',
      }}>Clock On</button>

      {sessions.length > 0 && (
        <>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#8a9ab0', marginBottom: 12 }}>
            Clocked On ({sessions.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sessions.map(s => {
              const isMe = myActiveSession && s.id === myActiveSession.id;
              return (
                <div key={s.id} style={{
                  background: isMe ? 'rgba(26,110,181,0.08)' : '#ffffff',
                  border: `1px solid ${isMe ? '#1a6eb5' : '#dde1e7'}`, borderRadius: 10, padding: '16px',
                  display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 18, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {s.staff_name}
                      {isMe && <span style={{ background: 'rgba(26,110,181,0.15)', color: '#1a6eb5', border: '1px solid rgba(26,110,181,0.4)', borderRadius: 3, padding: '1px 7px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>You</span>}
                    </div>
                    {s.non_productive ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ background: 'rgba(26,110,181,0.12)', color: '#1a6eb5', border: '1px solid rgba(26,110,181,0.3)', borderRadius: 3, padding: '1px 7px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>NON-PROD</span>
                        {(s.non_productive_reason || s.description) && <span style={{ fontSize: 13, color: '#4a5568' }}>{s.non_productive_reason || s.description}</span>}
                      </div>
                    ) : (
                      <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: '#1a6eb5', marginBottom: 4 }}>
                        {s.job_number} <span style={{ color: '#4a5568', fontFamily: 'IBM Plex Sans, sans-serif' }}>— {s.project_title}</span>
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#8a9ab0' }}>{s.work_centre_name} · {elapsed(s.started_at, now)} ago</div>
                  </div>
                  <button onClick={() => clockOff(s)} disabled={saving === s.id} style={{
                    padding: '14px 16px', border: 'none', borderRadius: 8, cursor: 'pointer',
                    background: saving === s.id ? '#ffffff' : '#c0392b', color: '#fff',
                    fontFamily: 'Rajdhani, sans-serif', fontSize: 15, fontWeight: 700,
                    letterSpacing: 1, textTransform: 'uppercase', minWidth: 88,
                    opacity: saving === s.id ? 0.6 : 1,
                  }}>
                    {saving === s.id ? '...' : 'Clock Off'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Today's summary strip ────────────────────────────────────────────────────
function TodaySummary({ logs }) {
  if (!logs.length) return null;
  const total = logs.reduce((s, l) => s + parseFloat(l.hours_decimal || 0), 0);
  const byWc  = logs.reduce((acc, l) => { acc[l.work_centre_name] = (acc[l.work_centre_name] || 0) + parseFloat(l.hours_decimal || 0); return acc; }, {});
  return (
    <div style={{ background: '#ffffff', border: '1px solid #1a6eb5', borderRadius: 10, padding: '14px 16px', marginBottom: 20, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
      <div>
        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: '#8a9ab0', letterSpacing: 2, textTransform: 'uppercase' }}>Today</div>
        <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 32, fontWeight: 700, color: '#1a6eb5', lineHeight: 1 }}>
          {total.toFixed(2)}<span style={{ fontSize: 13, color: '#8a9ab0', marginLeft: 4 }}>h</span>
        </div>
        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#8a9ab0', marginTop: 2 }}>{logs.length} entries</div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
        {Object.entries(byWc).map(([wc, h]) => (
          <div key={wc} style={{ background: '#ffffff', borderRadius: 8, padding: '6px 12px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: '#8a9ab0', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>{wc}</div>
            <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 16, fontWeight: 700, color: '#1a1d23' }}>{h.toFixed(2)}h</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Production page ──────────────────────────────────────────────────────────
function ShopFloorProduction({ staffId }) {
  const [projects,    setProjects]    = useState([]);
  const [workCentres, setWorkCentres] = useState([]);
  const [todayLogs,   setTodayLogs]   = useState([]);

  const loadMeta = useCallback(() => {
    api.get('/projects').then(r => setProjects((r.data.value || []).filter(p => !['delivered','invoiced'].includes(p.status))));
    api.get('/work-centres').then(r => setWorkCentres((r.data.value || []).filter(w => w.is_active)));
  }, []);
  const loadToday = useCallback(() => {
    api.get('/production-logs/today').then(r => setTodayLogs(r.data.value || []));
  }, []);

  useEffect(() => { loadMeta(); loadToday(); }, [loadMeta, loadToday]);

  return (
    <div>
      <TodaySummary logs={todayLogs} />
      <ClockForm projects={projects} workCentres={workCentres} staffId={staffId} onLogged={loadToday} />
    </div>
  );
}

// ─── Jobs page (read-only Kanban) ─────────────────────────────────────────────
function ShopFloorJobs() {
  return <KanbanBoard readOnly={true} />;
}

// ─── Section toggle ────────────────────────────────────────────────────────────
function ShopFloorNav() {
  const tabStyle = (isActive) => ({
    flex: 1, textAlign: 'center', padding: '12px 0', textDecoration: 'none', borderRadius: 8,
    color: isActive ? '#f5f6f8' : '#4a5568',
    background: isActive ? '#1a6eb5' : 'transparent',
    fontFamily: 'Rajdhani, sans-serif', fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
    transition: 'background 0.15s, color 0.15s',
  });
  return (
    <div style={{ display: 'flex', gap: 4, background: '#ffffff', border: '1px solid #dde1e7', borderRadius: 10, padding: 4, marginBottom: 20 }}>
      <NavLink to="/shopfloor" end style={({ isActive }) => tabStyle(isActive)}>Production</NavLink>
      <NavLink to="/shopfloor/jobs" style={({ isActive }) => tabStyle(isActive)}>Jobs</NavLink>
    </div>
  );
}

function LiveClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 30000); return () => clearInterval(id); }, []);
  return <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, color: '#4a5568' }}>
    {t.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })} · {t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
  </span>;
}

// ─── Main ShopFloor shell ─────────────────────────────────────────────────────
function ShopFloorInner({ onSignOut, staffId, staffName }) {
  return (
    <div style={{ background: '#f5f6f8', minHeight: '100vh', color: '#1a1d23' }}>
      <header style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, background: '#ffffff', borderBottom: '1px solid #dde1e7' }}>
        <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 20, fontWeight: 700, color: '#1a6eb5', letterSpacing: 2, textTransform: 'uppercase' }}>CGS</span>
            <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#8a9ab0', letterSpacing: 3, textTransform: 'uppercase', paddingTop: 2 }}>Shop Floor</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <LiveClock />
            <button onClick={onSignOut} style={{
              background: 'none', border: '1px solid #dde1e7', borderRadius: 6, cursor: 'pointer',
              color: '#4a5568', padding: '6px 10px',
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase',
            }}>
              ⏻ Sign Out
            </button>
          </div>
        </div>
        <div style={{ padding: '0 20px 10px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#1a6eb5', letterSpacing: 1 }}>
          Logged in as: {staffName}
        </div>
      </header>
      <div style={{ paddingTop: 104, paddingBottom: 32, paddingLeft: 16, paddingRight: 16, maxWidth: 600, margin: '0 auto' }}>
        <ShopFloorNav />
        <Routes>
          <Route path="/"     element={<ShopFloorProduction staffId={staffId} />} />
          <Route path="jobs"  element={<ShopFloorJobs />} />
        </Routes>
      </div>
    </div>
  );
}

export default function ShopFloor({ onSignOut, staffId, staffName }) {
  return <ShopFloorInner onSignOut={onSignOut} staffId={staffId} staffName={staffName} />;
}
